import { useQuery } from '@tanstack/react-query';
import { LaborMetricFilter, LaborMetricsInterval } from '__generated__/graphql';
import { IDashboardGraphData } from 'containers/dashboard/dashboard-grid/card/hooks/useDashboardCardTotal';
import { DashboardTimeFrame } from 'containers/dashboard/types/types';
import { gql } from 'graphql-request';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import useReactQueryBaseKey from 'hooks/react-query/useReactQueryBaseKey/useReactQueryBaseKey';
import { DateTime } from 'luxon';
import IJitLaborMetric from 'types/aggregate/JitLaborMetric';
import ITimeRange from 'types/TimeRange';
import { ReportType } from 'utils/aggregateUtils';
import { getMonthsBetween } from 'utils/dateUtils';
import { getMetricLaborTotals, getMetricTimeTotals } from 'utils/jitMetricUtils';

const DASHBOARD_EQUIPMENT_LABOR_METRIC_BARS_QUERY = gql`
  query DashboardEquipmentLaborMetricBars(
    $ranges: [DashboardRangeInput!]!
    $interval: LaborMetricsInterval!
    $filter: LaborMetricFilter
  ) {
    dashboard {
      equipmentLaborMetricBars(ranges: $ranges, interval: $interval, filter: $filter) {
        startTime
        endTime
        rt
        ot
        dt
        addOnTime
        rtCents
        otCents
        dtCents
        overburdenRtCents
        overburdenOtCents
        overburdenDtCents
        equipmentCents
      }
    }
  }
`;

type EquipmentLaborMetricBar = Pick<
  IJitLaborMetric,
  | 'rt'
  | 'ot'
  | 'dt'
  | 'rtCents'
  | 'otCents'
  | 'dtCents'
  | 'overburdenRtCents'
  | 'overburdenOtCents'
  | 'overburdenDtCents'
> & {
  addOnTime: number;
  equipmentCents: number;
};

function intervalFor(timeRange: ITimeRange<DateTime>, timeFrame: DashboardTimeFrame): LaborMetricsInterval {
  if (timeFrame === 'payPeriod') {
    return LaborMetricsInterval.PayPeriod;
  }
  const monthLimit = timeFrame === 'weekly' ? 4 : 2;
  const months = getMonthsBetween(timeRange.startTime, timeRange.endTime);
  return months.length > monthLimit ? LaborMetricsInterval.Month : LaborMetricsInterval.Day;
}

function barToTimeData(bar: EquipmentLaborMetricBar, day: number): IDashboardGraphData {
  const totals = getMetricTimeTotals(bar as unknown as IJitLaborMetric);
  return {
    day,
    total: totals.totalSeconds / 3600,
    regular: (totals.regularSeconds + totals.paidTimeOffSeconds) / 3600,
    overtime: totals.overtimeSeconds / 3600,
    doubletime: totals.doubleTimeSeconds / 3600,
    totalWithAddOn: totals.totalWithAddOnSeconds / 3600,
    addOn: totals.addOnTimeSeconds / 3600,
  };
}

function barToCostData(bar: EquipmentLaborMetricBar, day: number): IDashboardGraphData {
  const totals = getMetricLaborTotals(bar as unknown as IJitLaborMetric);
  return {
    day,
    total: totals.totalCost,
    regular: totals.regularCost + totals.timeOffCost,
    overtime: totals.overtimeCost,
    doubletime: totals.doubleTimeCost,
    totalEquipmentCost: totals.totalCost + totals.equipmentCost,
  };
}

export default function useDashboardEquipmentLaborMetricBars(
  timeRange: ITimeRange<DateTime>,
  timeRanges: Array<ITimeRange<number>>,
  timeFrame: DashboardTimeFrame,
  reportType: ReportType,
  metricFilter: LaborMetricFilter | undefined,
  enabled: boolean
) {
  const graphQLClient = useGraphQLClient();
  const baseKey = useReactQueryBaseKey();

  const interval = intervalFor(timeRange, timeFrame);
  const ranges = timeRanges.map((range) => ({
    startTime: DateTime.fromSeconds(range.startTime, { zone: 'utc' }).toISO({
      suppressMilliseconds: true,
      includeOffset: false,
    }),
    endTime: DateTime.fromSeconds(range.endTime, { zone: 'utc' }).toISO({
      suppressMilliseconds: true,
      includeOffset: false,
    }),
  }));

  const rangesKey = JSON.stringify(timeRanges);
  const filterKey = JSON.stringify(metricFilter ?? null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [...baseKey, 'dashboard-equipment-labor-metric-bars', { interval, rangesKey, filterKey, reportType }],
    queryFn: async () => {
      const result = await graphQLClient.request<{
        dashboard: { equipmentLaborMetricBars: EquipmentLaborMetricBar[] };
      }>({
        document: DASHBOARD_EQUIPMENT_LABOR_METRIC_BARS_QUERY,
        variables: { ranges, interval, filter: metricFilter ?? null },
      });

      const bars = result.dashboard.equipmentLaborMetricBars;
      return timeRanges.map((range, index) => {
        const bar = bars[index];
        const mapper = reportType === ReportType.COST ? barToCostData : barToTimeData;
        return bar ? mapper(bar, range.startTime) : { day: range.startTime, total: 0 };
      });
    },
    enabled,
  });

  return { data: data ?? [], isLoading, error, refetch };
}
